import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";

// API URL
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

// Initial state
const initialState = {
  giveaways: [],
  giveaway: null,
  participants: null,
  loading: false,
  error: null,
  totalGiveaways: 0,
  totalPages: 0,
  currentPage: 1,
  filters: {
    status: "",
    type: "",
  },
};

// Get all giveaways
export const getGiveaways = createAsyncThunk(
  "giveaways/getGiveaways",
  async (
    { page = 1, perPage = 20, search = "", status = "", type = "", sortBy = "" },
    { rejectWithValue }
  ) => {
    try {
      let url = `${API_URL}/giveaways?page=${page}&per_page=${perPage}`;

      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }

      if (status) {
        url += `&status=${status}`;
      }

      if (type) {
        url += `&type=${type}`;
      }

      if (sortBy) {
        url += `&sortBy=${sortBy}`;
      }

      const response = await axios.get(url);
      
      // Handle both old and new pagination response formats
      const responseData = response.data;
      const pagination = responseData.pagination || {
        page: page,
        per_page: perPage,
        total: parseInt(responseData?.totalGiveaways || 0),
        total_pages: parseInt(responseData?.totalPages || 0)
      };
      
      return {
        giveaways: responseData?.giveaways || [],
        pagination: {
          page: pagination.page,
          per_page: pagination.per_page,
          total: pagination.total,
          total_pages: pagination.total_pages
        },
        // Keep backward compatibility
        totalGiveaways: pagination.total,
        totalPages: pagination.total_pages,
        currentPage: pagination.page,
        filters: { search, status, type, sortBy },
      };
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to fetch giveaways";
      // toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Get giveaway by ID
export const getGiveawayById = createAsyncThunk(
  "giveaways/getGiveawayById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/giveaways/${id}`);
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to fetch giveaway";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Create giveaway
export const createGiveaway = createAsyncThunk(
  "giveaways/createGiveaway",
  async (giveawayData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/giveaways`, giveawayData);
      toast.success("Giveaway created successfully");
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to create giveaway";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Update giveaway
export const updateGiveaway = createAsyncThunk(
  "giveaways/updateGiveaway",
  async ({ id, giveawayData }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${API_URL}/giveaways/${id}`,
        giveawayData
      );
      toast.success("Giveaway updated successfully");
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to update giveaway";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Draw winner
export const drawWinner = createAsyncThunk(
  "giveaways/drawWinner",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/giveaways/${id}/draw`);
      toast.success("Winner drawn successfully!");
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to draw winner";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Start giveaway (create and draw winner immediately)
export const startGiveaway = createAsyncThunk(
  "giveaways/startGiveaway",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/giveaways`);
      toast.success("Giveaway started and winner drawn successfully!");
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to start giveaway";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Get giveaway participants
export const getGiveawayParticipants = createAsyncThunk(
  "giveaways/getGiveawayParticipants",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_URL}/giveaways/${id}/participants`
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to fetch participants";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Get active giveaways
export const getActiveGiveaways = createAsyncThunk(
  "giveaways/getActiveGiveaways",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/giveaways/active`);
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to fetch active giveaways";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Get past giveaways
export const getPastGiveaways = createAsyncThunk(
  "giveaways/getPastGiveaways",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/giveaways/past`);
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to fetch past giveaways";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Get member entered giveaways
export const getMemberEnteredGiveaways = createAsyncThunk(
  "giveaways/getMemberEnteredGiveaways",
  async (memberId, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${API_URL}/members/${memberId}/giveaways`
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        "Failed to fetch member entered giveaways";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Send winner email
export const sendWinnerEmail = createAsyncThunk(
  "giveaways/sendWinnerEmail",
  async ({ giveawayId, subject, body }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_URL}/notifications/send-winner-email`,
        {
          giveawayId,
          subject,
          body
        }
      );
      toast.success("Winner email sent successfully!");
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to send winner email";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Send participant email
export const sendParticipantEmail = createAsyncThunk(
  "giveaways/sendParticipantEmail",
  async ({ giveawayId, subject, body }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_URL}/notifications/send-participant-email`,
        {
          giveawayId,
          subject,
          body
        }
      );
      toast.success("Participant emails sent successfully!");
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to send participant emails";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

// Giveaways slice
const giveawaysSlice = createSlice({
  name: "giveaways",
  initialState,
  reducers: {
    resetGiveawaysError: (state) => {
      state.error = null;
    },
    clearGiveaway: (state) => {
      state.giveaway = null;
    },
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get giveaways cases
      .addCase(getGiveaways.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getGiveaways.fulfilled, (state, action) => {
        state.loading = false;
        state.giveaways = Array.isArray(action.payload.giveaways)
          ? action.payload.giveaways
          : [];
        state.totalGiveaways = action.payload.totalGiveaways;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
        state.filters = action.payload.filters;
        state.error = null;
      })
      .addCase(getGiveaways.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get giveaway by ID cases
      .addCase(getGiveawayById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getGiveawayById.fulfilled, (state, action) => {
        state.loading = false;
        state.giveaway = action.payload;
        state.error = null;
      })
      .addCase(getGiveawayById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create giveaway cases
      .addCase(createGiveaway.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createGiveaway.fulfilled, (state, action) => {
        state.loading = false;
        state.giveaway = action.payload;
        state.error = null;
      })
      .addCase(createGiveaway.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update giveaway cases
      .addCase(updateGiveaway.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateGiveaway.fulfilled, (state, action) => {
        state.loading = false;
        state.giveaway = action.payload;

        // Update giveaway in giveaways list if it exists
        const index = state.giveaways.findIndex(
          (g) => g.id === action.payload.id
        );
        if (index !== -1) {
          state.giveaways[index] = action.payload;
        }

        state.error = null;
      })
      .addCase(updateGiveaway.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Draw winner cases
      .addCase(drawWinner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(drawWinner.fulfilled, (state, action) => {
        state.loading = false;
        state.giveaway = action.payload;

        // Update giveaway in giveaways list if it exists
        const index = state.giveaways.findIndex(
          (g) => g.id === action.payload.id
        );
        if (index !== -1) {
          state.giveaways[index] = action.payload;
        }

        state.error = null;
      })
      .addCase(drawWinner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Start giveaway cases
      .addCase(startGiveaway.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(startGiveaway.fulfilled, (state, action) => {
        state.loading = false;
        const newGiveaway = action.payload.giveaway;
        // Ensure giveaways is an array before using unshift
        if (!Array.isArray(state.giveaways)) {
          state.giveaways = [];
        }
        state.giveaways.unshift(newGiveaway); // Add to beginning of list
        state.giveaway = newGiveaway;
        state.totalGiveaways += 1;
        state.error = null;
      })
      .addCase(startGiveaway.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get active giveaways cases
      .addCase(getActiveGiveaways.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getActiveGiveaways.fulfilled, (state, action) => {
        state.loading = false;
        state.giveaways = Array.isArray(action.payload) ? action.payload : [];
        state.error = null;
      })
      .addCase(getActiveGiveaways.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get past giveaways cases
      .addCase(getPastGiveaways.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPastGiveaways.fulfilled, (state, action) => {
        state.loading = false;
        state.giveaways = Array.isArray(action.payload) ? action.payload : [];
        state.error = null;
      })
      .addCase(getPastGiveaways.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get member entered giveaways cases
      .addCase(getMemberEnteredGiveaways.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMemberEnteredGiveaways.fulfilled, (state, action) => {
        state.loading = false;
        state.giveaways = Array.isArray(action.payload) ? action.payload : [];
        state.error = null;
      })
      .addCase(getMemberEnteredGiveaways.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get giveaway participants cases
      .addCase(getGiveawayParticipants.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getGiveawayParticipants.fulfilled, (state, action) => {
        state.loading = false;
        state.participants = action.payload;
        state.error = null;
      })
      .addCase(getGiveawayParticipants.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Send winner email cases
      .addCase(sendWinnerEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendWinnerEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.giveaway = action.payload;

        // Update giveaway in giveaways list if it exists
        const index = state.giveaways.findIndex(
          (g) => g.id === action.payload.id
        );
        if (index !== -1) {
          state.giveaways[index] = action.payload;
        }

        state.error = null;
      })
      .addCase(sendWinnerEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Send participant email cases
      .addCase(sendParticipantEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendParticipantEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.giveaway = action.payload;

        // Update giveaway in giveaways list if it exists
        const index = state.giveaways.findIndex(
          (g) => g.id === action.payload.id
        );
        if (index !== -1) {
          state.giveaways[index] = action.payload;
        }

        state.error = null;
      })
      .addCase(sendParticipantEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetGiveawaysError, clearGiveaway, setFilters } =
  giveawaysSlice.actions;

export default giveawaysSlice.reducer;
